import React from 'react';
import { Breadcrumb } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const SEGMENT_KEYS: Record<string, string> = {
  lobby: 'breadcrumb.lobby',
  create: 'breadcrumb.create',
  join: 'breadcrumb.join',
  game: 'breadcrumb.game',
  basic: 'breadcrumb.basic',
  'single-player': 'breadcrumb.singlePlayer',
  rules: 'breadcrumb.rules',
  about: 'breadcrumb.about',
};

export const BreadcrumbNav: React.FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  // Strip language prefix (/uk or /pl) before building breadcrumbs
  const rawSegments = pathname.split('/').filter(Boolean);
  const isLangPrefix = rawSegments[0] === 'uk' || rawSegments[0] === 'pl';
  const langPfx = isLangPrefix ? `/${rawSegments[0]}` : '';
  const segments = isLangPrefix ? rawSegments.slice(1) : rawSegments;

  function segmentLabel(seg: string): string {
    if (SEGMENT_KEYS[seg]) return t(SEGMENT_KEYS[seg]!);
    // UUID-like game IDs — show short form
    if (/^[0-9a-f-]{8,}$/i.test(seg)) return t('breadcrumb.gameShort', { id: seg.slice(0, 8) });
    return seg;
  }

  // Don't render on top-level pages (lobby home, rules, about)
  if (segments.length <= 1) return null;

  const items: { title: React.ReactNode }[] = [
    { title: <Link to={langPfx + '/lobby'}><HomeOutlined /> {t('breadcrumb.lobby')}</Link> },
  ];

  // Skip the first segment ("lobby") if it's already represented as home
  const rest = segments[0] === 'lobby' ? segments.slice(1) : segments;
  const baseSegments = segments[0] === 'lobby' ? ['lobby'] : [];

  rest.forEach((seg, i) => {
    const pathSegs = [...baseSegments, ...rest.slice(0, i + 1)];
    const path = langPfx + '/' + pathSegs.join('/');
    const isLast = i === rest.length - 1;

    items.push({
      title: isLast ? segmentLabel(seg) : <Link to={path}>{segmentLabel(seg)}</Link>,
    });
  });

  return (
    <Breadcrumb
      items={items}
      className="breadcrumb-nav"
      style={{ marginBottom: 16, fontSize: 'clamp(12px, 2.5vw, 14px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
    />
  );
};

export default BreadcrumbNav;
